import React, { useState, useEffect } from "react";
import {
  CurrentSubjectsContainer,
  DashboardContainer,
  DashboardRow,
  ScrollViewStyled,
  SubjectInfoContainerStyled,
  SubjectResumeStyled,
  SubjectTitleStyled,
  UserCardInfo,
  UserCardInfoContainer,
  UserCardStyled,
  UserInfoContainerStyled,
  UserInfoLabel,
  UserInfoText,
  UserInfoTextBlue,
} from "./dashboard.styled";
import { Space, Image, Box, EmptyState } from "@nubeteck/components";
import { DocSVG, TextIconSVG, UserSVG } from "../../assets";
import { ActionCard, WarningCard, WelcomeCard } from "../../components/custom/cards";
import { useSelector } from "react-redux";
import { RootState } from "@/core/store/store";
import { useGetCurrentSubjectsQuery, useGetDashboardCoursesMutation } from "src/services";
import { ISubject } from "@/Interfaces/subject";
import { Skeleton } from "antd";
import { CardStyled } from "src/components/custom/cards/card.styled";
import { getRolesByToken } from "src/utils";
import { useNavigate } from "react-router";
import { FlexStyled } from "src/components/registration/register-introduction/register-introduction.styled";
import { getHomeAutoservicioPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { getHomeStudentAutoservicePage } from "../../Api/content";
import { getHomeProfesorAutoservicePage } from "../../Api/content";
import { StrapiError } from "../../pages/StrapiError";

interface IUserCardInfoItemProps {
  label: string;
  content: string;
}

const UserCardInfoItem = ({ label, content }: IUserCardInfoItemProps) => (
  <Space direction="vertical" style={{ minInlineSize: 125 }}>
    <UserInfoLabel type="secondary">{label}</UserInfoLabel>
    <UserInfoText>{content}</UserInfoText>
  </Space>
);

const UserCard = ({ homeAutoservicioPage }: { homeAutoservicioPage: any }) => {
  const { userInfo } = useSelector((state: RootState) => state.user);
  return (
    <UserCardStyled
      style={{ flex: 1 }}
      bodyStyle={{
        padding: 0,
      }}
    >
      <UserCardInfoContainer>
        <UserCardInfo>
          <UserCardInfoItem label={homeAutoservicioPage?.dashboardO || "ID"} content={userInfo.id} />
          <UserCardInfoItem label={homeAutoservicioPage?.dashboardT || "Correo electrónico"} content={userInfo.correoElectronico} />
          <UserCardInfoItem label={homeAutoservicioPage?.dashboardTR || "Teléfono"} content={userInfo.telefono} />
        </UserCardInfo>
        <UserInfoContainerStyled>
          <Image preview={false} src={UserSVG} width={65} />
          <UserInfoTextBlue>
            {userInfo?.fullName ?? `${userInfo.nombres} ${userInfo.apellidos}`}
          </UserInfoTextBlue>
          <Image preview={false} src={TextIconSVG} width={65} />
        </UserInfoContainerStyled>
      </UserCardInfoContainer>
    </UserCardStyled>
  );
};

const CurrentSubjects = ({ 
  homeStudentAutoservicePage, 
  homeProfesorAutoservicePage 
}: { 
  homeStudentAutoservicePage?: any; 
  homeProfesorAutoservicePage?: any; 
}) => {
  const {
    data: materiasActuales,
    isLoading,
    refetch,
  } = useGetCurrentSubjectsQuery("", {
    refetchOnMountOrArgChange: true,
  });
  const [getTeacherCourses, { data: materiasActualesProfesor, isLoadingProf }] =
    useGetDashboardCoursesMutation("");
  const navigate = useNavigate();
  const { isATeacher } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    refetch();
  }, [refetch]);
  useEffect(() => {
    getTeacherCourses();
  }, [getTeacherCourses]);
  return (
    <CurrentSubjectsContainer>
      <SubjectTitleStyled>
        {isATeacher ? homeProfesorAutoservicePage?.title || "Clases asignadas" : `${homeStudentAutoservicePage?.title || "Materias en curso"}`}
      </SubjectTitleStyled>
      <ScrollViewStyled>
        <Skeleton loading={isLoading || isLoadingProf} active round />
        <Skeleton loading={isLoading || isLoadingProf} active round />
        {!isLoading &&
        (materiasActuales?.data?.length > 0 || materiasActualesProfesor?.length > 0) ? (
          (isATeacher ? materiasActualesProfesor : materiasActuales?.data)?.map(
            (materia: ISubject & { seccionId: number }) => (
              <SubjectResumeStyled
                key={materia.asignaturaId}
                onClick={
                  isATeacher
                    ? () => {
                        navigate(`/teacher/courses/${materia.seccionId}`);
                      }
                    : () => {
                        navigate("/subjects-list");
                      }
                }
              >
                <SubjectInfoContainerStyled>
                  <Box>
                    <h4>{`${materia.seccion ? materia.seccion : ""} - ${
                      materia.asignaturaNombre
                    }`}</h4>
                    <FlexStyled style={{ gap: 20 }}>
                      <span>{materia.asignaturaCodigo}</span>
                      {materia.periodo && (
                        <span style={{ color: "#00479B", fontWeight: "bold" }}>
                          {" "}
                          | {materia.periodo}
                        </span>
                      )}
                    </FlexStyled>
                  </Box>
                  <Image preview={false} src={DocSVG} alt="Ilustración documento" />
                </SubjectInfoContainerStyled>
              </SubjectResumeStyled>
            ),
          )
        ) : (
          <CardStyled>
            <EmptyState
              emptyText={
                isATeacher
                  ? homeProfesorAutoservicePage?.empty_message || "No tiene asignado ningún curso en este periodo."
                  : homeStudentAutoservicePage?.nosubject_message || "No está cursando ninguna materia en este periodo"
              }
            />
          </CardStyled>
        )}
      </ScrollViewStyled>
    </CurrentSubjectsContainer>
  );
};

const Dashboard = () => {
  const roles = getRolesByToken(localStorage.getItem("token") ?? "");
  const esAdmin =
    roles.includes("administrador(a) del sistema") ||
    roles.includes("cajero") ||
    roles.includes("vicerrectoria") ||
    roles.includes("matricula administrativa");
  const { isATeacher } = useSelector((state: RootState) => state.user);
  const { userInfo } = useSelector((state: RootState) => state.user);
  const { locale } = useLocale();
  const [homeAutoservicioPage, setHomeAutoservicioPage] = useState<any>(null);
  const [homeStudentAutoservicePage, setHomeStudentAutoservicePage] = useState<any>(null);
  const [homeProfesorAutoservicePage, setHomeProfesorAutoservicePage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setHomeAutoservicioPage(null);
        setHomeStudentAutoservicePage(null);
        setHomeProfesorAutoservicePage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    const loadAllContent = async () => {
      try {
        const [homeData, studentData, teacherData] = await Promise.all([
          getHomeAutoservicioPage(locale),
          getHomeStudentAutoservicePage(locale),
          getHomeProfesorAutoservicePage(locale)
        ]);

        if (!cancel) {
          clearTimeout(timeoutId);
          setHomeAutoservicioPage(homeData?.data?.attributes || homeData?.data || homeData);
          setHomeStudentAutoservicePage(studentData?.data?.attributes || studentData?.data || studentData);
          setHomeProfesorAutoservicePage(teacherData?.data?.attributes || teacherData?.data || teacherData);
          setIsLoadingContent(false);
        }
      } catch (error) {
        if (!cancel) {
          clearTimeout(timeoutId);
          setHomeAutoservicioPage(null);
          setHomeStudentAutoservicePage(null);
          setHomeProfesorAutoservicePage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      }
    };

    loadAllContent();
    
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const navigate = useNavigate();

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setHomeAutoservicioPage(null);
        setHomeStudentAutoservicePage(null);
        setHomeProfesorAutoservicePage(null);
      }}
    />;
  }

  return (
    <DashboardContainer>
      {isLoadingContent ? (
        <>
          <DashboardRow>
            <div style={{ flex: 1 }}>
              <Skeleton.Input active size="large" style={{ width: "100%", marginBottom: 16 }} />
              <Skeleton.Input active size="default" style={{ width: "80%" }} />
            </div>
            <div style={{ flex: 1 }}>
              <Skeleton.Input active size="large" style={{ width: "100%", marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: "90%", marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: "70%" }} />
            </div>
          </DashboardRow>
          <DashboardRow>
            <div style={{ flex: 1 }}>
              <Skeleton.Input active size="large" style={{ width: "100%", marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: "90%", marginBottom: 16 }} />
              <Skeleton.Input active size="default" style={{ width: 200 }} />
            </div>
            <div style={{ flex: 1 }}>
              <Skeleton.Input active size="large" style={{ width: "100%", marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: "85%", marginBottom: 16 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </div>
          </DashboardRow>
        </>
      ) : (
        <>
          <DashboardRow>
            <WelcomeCard
              welcomeText={
                isATeacher
                  ? homeProfesorAutoservicePage?.subtitle || "Gestione la información correspondiente a sus cursos de forma ágil y sencilla."
                  : esAdmin
                  ? homeAutoservicioPage?.description || "Gestione la información correspondiente al proceso de matrícula de forma ágil y sencilla"
                  : homeStudentAutoservicePage?.subtitle || "Consulta tu calendario, tus reportes, y matricúlate de forma ágil y sencilla."
              }
            />
            <UserCard homeAutoservicioPage={homeAutoservicioPage} />
          </DashboardRow>
          <DashboardRow>
            {!esAdmin || isATeacher ? (
              <CurrentSubjects 
                homeStudentAutoservicePage={homeStudentAutoservicePage}
                homeProfesorAutoservicePage={homeProfesorAutoservicePage}
              />
            ) : (
              <ActionCard
                title={homeAutoservicioPage?.subtitle || "Configure y gestione los datos del sistema en sencillos pasos."}
                subtitle={
                  roles.includes("administrador(a) del sistema")
                    ? homeAutoservicioPage?.descriptionT || "Configure los formularios de las solicitudes de servicios."
                    : roles.includes("cajero")
                    ? "Realice la validación de pagos en caja."
                    : roles.includes("matricula administrativa")
                    ? homeAutoservicioPage?.descriptionT || "Realice la matriculación para un estudiante."
                    : roles.includes("vicerrectoria")
                    ? homeAutoservicioPage?.do_imports || "Realice las importaciones de datos."
                    : homeAutoservicioPage?.do_sponsorships || "Asigne los patrocinios correspondientes a los estudiantes."
                }
                actionName={
                  roles.includes("administrador(a) del sistema")
                    ? homeAutoservicioPage?.config_button || "Ir a la configuración de solicitudes"
                    : roles.includes("cajero")
                    ? "Ir a caja"
                    : roles.includes("matricula administrativa")
                    ? homeProfesorAutoservicePage?.prenrolled_button || "Ir a pre-matricula."
                    : roles.includes("vicerrectoria")
                    ? homeAutoservicioPage?.go_imports || "Ir a importaciones"
                    : homeAutoservicioPage?.go_sponsorships || "Ir a patrocinios"
                }
                action={
                  roles.includes("administrador(a) del sistema")
                    ? () => navigate("/admin/requests")
                    : roles.includes("cajero")
                    ? () => navigate("/admin/charges")
                    : roles.includes("matricula administrativa")
                    ? () => navigate("/admin/user-selection")
                    : roles.includes("vicerrectoria")
                    ? () => navigate("/admin/data-imports")
                    : () => navigate("/admin/sponsorships")
                }
              />
            )}
            {isATeacher ? (
              <ActionCard
                title={homeProfesorAutoservicePage?.subtitle || "Administre sus clases en sencillos pasos"}
                subtitle={homeProfesorAutoservicePage?.message || "Gestione la lista de clase, calificaciones y actividades."}
                actionName={homeProfesorAutoservicePage?.courses_button || "Ir a mis cursos"}
                action={() => navigate("teacher/courses")}
              />
            ) : esAdmin ? (
              <></>
            ) : userInfo?.estaSuspendido ? (
              <WarningCard
                size="large"
                title={homeAutoservicioPage?.suspended_title || "En este momento estás suspendido y no podrás utilizar esta herramienta."}
                message={homeAutoservicioPage?.suspended_message || "Comunícate con tu escuela para más información"}
              />
            ) : userInfo?.hayProcesoAbierto ? (
              <ActionCard
                title={homeStudentAutoservicePage?.subtitleT || "¡Empieza el proceso de matrícula!"}
                subtitle={homeStudentAutoservicePage?.description || "Selecciona tus materias y realiza el pago en sencillos pasos."}
                actionName={homeStudentAutoservicePage?.start_button || "Empezar"}
                action={() => navigate("/subjects-registration/initial")}
              />
            ) : (
              <ActionCard
                title={homeStudentAutoservicePage?.subtitleTR || "Proceso de matricula cerrado"}
                subtitle={homeStudentAutoservicePage?.descriptionT || "Espera a que se abra un periodo de matricula y selecciona tus asignaturas correspondientes."}
              />
            )}
          </DashboardRow>
        </>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
